import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Map, { Source, Layer, Popup } from 'react-map-gl';
import {
  CircularProgress,
  Box,
  IconButton,
  ToolTip,
  SettingsOutlinedIcon,
  TuneIcon
} from '@esgian/esgianui';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getThemeMode } from '@store/features';
import MapFullscreenButton, {
  mapFullscreenButtonParentStyle
} from '@components/Common/MapFullscreenButton';
import { useTimezone } from '@hooks/useTimezone';
import {
  createVesselGeoJson,
  createPopupContent,
  createVesselIcon
} from '../../Sections/ShipAnalytics/DashboardActivityMapSection/utils/map';
import 'mapbox-gl/dist/mapbox-gl.css';
import VesselSpeedLegend from './VesselSpeedLegend';
import { useTransitDetails } from './useTransitDetails';
import {
  VESSEL_DEFAULT_ICON_COLOR,
  LOW_SPEED_VESSEL_ICON_COLOR,
  MEDIUM_SPEED_VESSEL_ICON_COLOR,
  HIGH_SPEED_VESSEL_ICON_COLOR
} from '@helpers';
import { ROUTE_LINE_STYLES, ROUTE_COLORS } from '@constants/mapConstants';
import VesselsMapSettings from './VesselsMapSettings';
import DashboardActivityMapZoomControls from '../DashboardActivityMap/DashboardActivityMapZoomControls';
import VesselsMapFilter from './VesselsMapFilter';
import VesselsMapRouteDurationSelector from './VesselsMapRouteDurationSelector';
import { useSegment, useLookupPortsQuery } from '@hooks';
const OVERVIEW_MAP_FULLSCREEN_PARAM = 'overviewMapFullscreen';

function VesselsMap({
  vessels = [],
  canals = [],
  projection = 'globe',
  height = '100%',
  zoom = 1,
  center = [0, 0],
  showSpeedLegend = false,
  loading = false,
  isFullscreen = false,
  allowFullscreen = false,
  selectedCanal = null,
  onCanalClick,
  isCanal,
  regionPolygon
}) {
  const [viewport, setViewport] = useState({
    longitude: center[0],
    latitude: center[1],
    zoom: zoom
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const isFullscreenFromUrl = useMemo(
    () => searchParams.get(OVERVIEW_MAP_FULLSCREEN_PARAM) === 'true',
    [searchParams]
  );

  const [selectedVessel, setSelectedVessel] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const themeMode = useSelector(getThemeMode);
  const { getTimeZoneDisplay } = useTimezone();
  const { uriExt } = useSegment();
  const [showVesselsName, setShowVesselsName] = useState(true);
  const mapContainer = React.useRef(null);
  const [selectedDays, setSelectedDays] = useState(null);
  const [showPorts, setShowPorts] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [key, setKey] = useState(0);

  const { data: lookupPorts } = useLookupPortsQuery();

  const [selectedVessels, setSelectedVessels] = useState(() => {
    return (vessels ?? []).map((v) => v.imo);
  });

  // Force map reload when theme changes
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [themeMode]);

  const memoizedCenter = useMemo(() => {
    return {
      longitude: center[0],
      latitude: center[1]
    };
  }, [center]);

  const latitude = useMemo(() => {
    return memoizedCenter.latitude;
  }, [memoizedCenter]);

  const longitude = useMemo(() => {
    return memoizedCenter.longitude;
  }, [memoizedCenter]);

  useEffect(() => {
    if (memoizedCenter) {
      setViewport({
        ...viewport,
        latitude,
        longitude
      });
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (zoom) {
      setViewport({
        ...viewport,
        zoom: zoom
      });
    }
  }, [zoom]);

  useEffect(() => {
    if (vessels?.length) {
      setSelectedVessels(vessels.map((v) => v.imo));
    }
  }, [vessels]);

  const vesselsImos = useMemo(() => (vessels ?? []).map((v) => v.imo), [vessels]);
  const { data: transitDetails, isFetching: isLoadingTransitDetails } = useTransitDetails({
    imos: vesselsImos ?? [],
    transitDays: selectedDays
  });

  const vesselGeoJson = React.useMemo(() => {
    const filteredVessels = vessels?.filter((v) => selectedVessels.includes(v.imo)) ?? [];
    return createVesselGeoJson(filteredVessels, false, true);
  }, [vessels, selectedVessels]);

  const [hoveredRouteId, setHoveredRouteId] = useState(null);

  // Add event handlers for route hover
  const onMouseEnterRoute = useCallback(
    (event) => {
      if (event.features.length > 0) {
        const map = event.target;
        map.getCanvas().style.cursor = 'pointer';

        if (hoveredRouteId !== null) {
          map.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });
        }

        const id = event.features[0].id;
        setHoveredRouteId(id);

        map.setFeatureState({ source: 'routes', id: id }, { hover: true });
      }
    },
    [hoveredRouteId]
  );

  const onMouseLeaveRoute = useCallback(
    (event) => {
      if (hoveredRouteId !== null) {
        const map = event.target;
        map.getCanvas().style.cursor = '';

        map.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });

        setHoveredRouteId(null);
      }
    },
    [hoveredRouteId]
  );

  const routeGeoJson = React.useMemo(() => {
    if (!transitDetails?.length) return null;

    return {
      type: 'FeatureCollection',
      features: transitDetails
        .filter((detail) => selectedVessels.includes(detail.voyageOverview?.imo))
        .map((detail, index) => {
          const route = detail.voyageReducedTransitDetails?.transitDetails;
          if (!route?.length) return null;

          // Sort route points by timestamp
          const sortedRoute = [...route].sort(
            (a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
          );

          // Handle date line crossing
          let prevLon = sortedRoute[0].lon;
          const adjustedCoordinates = sortedRoute.map((point) => {
            let lon = point.lon;

            // Adjust longitude when crossing the date line
            while (Math.abs(lon - prevLon) > 180) {
              lon += lon < prevLon ? 360 : -360;
            }
            prevLon = lon;

            return [lon, point.lat];
          });

          return {
            type: 'Feature',
            id: index, // Add unique id for each route to support feature state
            properties: {
              color: ROUTE_COLORS[index % ROUTE_COLORS.length],
              imo: detail.voyageOverview.imo
            },
            geometry: {
              type: 'LineString',
              coordinates: adjustedCoordinates
            }
          };
        })
        .filter(Boolean)
    };
  }, [transitDetails, selectedVessels]);

  const portGeoJson = React.useMemo(() => {
    if (!transitDetails?.length) return null;

    const ports = transitDetails
      .filter((detail) => selectedVessels.includes(detail.voyageOverview?.imo))
      .flatMap((detail) => detail.voyagePortCalls?.portCalls ?? [])
      .filter((port) => port.portLat && port.portLon);

    return {
      type: 'FeatureCollection',
      features: ports.map((port) => ({
        type: 'Feature',
        properties: {
          id: port.portCallId,
          name: port.portName,
          code: port.portCode
        },
        geometry: {
          type: 'Point',
          coordinates: [port.portLon, port.portLat]
        }
      }))
    };
  }, [transitDetails, selectedVessels]);

  const canalsGeoJson = React.useMemo(() => {
    if (!canals?.length) return null;

    return {
      type: 'FeatureCollection',
      features: canals
        .map((canal) => {
          if (canal.lat !== null && canal.long !== null) {
            return {
              type: 'Feature',
              properties: {
                id: canal.regionId,
                name: canal.name,
                unlocode: canal.unlocode,
                regionId: canal.regionId,
                markerType: 'canal',
                iconImage: 'canal-pin'
              },
              geometry: {
                type: 'Point',
                coordinates: [canal.long, canal.lat]
              }
            };
          }
          return null;
        })
        .filter(Boolean)
    };
  }, [canals]);

  const selectedCanalPolygon = React.useMemo(() => {
    if (!selectedCanal?.polygon) return null;

    try {
      const coordinates = selectedCanal.polygon
        .replace('POLYGON ((', '')
        .replace('))', '')
        .split(', ')
        .map((coord) => {
          const [lon, lat] = coord.split(' ');
          return [parseFloat(lon), parseFloat(lat)];
        });

      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              id: selectedCanal.id,
              name: selectedCanal.name
            },
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates]
            }
          }
        ]
      };
    } catch (error) {
      console.error('Error parsing canal polygon:', error);
      return null;
    }
  }, [selectedCanal]);

  const regionPolygonGeoJson = React.useMemo(() => {
    if (!isCanal || !regionPolygon) return null;

    try {
      const coordinates = regionPolygon
        .replace('POLYGON ((', '')
        .replace('))', '')
        .split(', ')
        .map((coord) => {
          const [lon, lat] = coord.split(' ');
          return [parseFloat(lon), parseFloat(lat)];
        });

      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              name: 'Region'
            },
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates]
            }
          }
        ]
      };
    } catch (error) {
      console.error('Error parsing region polygon:', error);
      return null;
    }
  }, [isCanal, regionPolygon]);

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  const updateMapIcons = useCallback((map) => {
    if (!map) return;

    // Add vessel icons for different speeds/states
    const defaultIcon = createVesselIcon(VESSEL_DEFAULT_ICON_COLOR);
    const slowIcon = createVesselIcon(LOW_SPEED_VESSEL_ICON_COLOR);
    const mediumIcon = createVesselIcon(MEDIUM_SPEED_VESSEL_ICON_COLOR);
    const fastIcon = createVesselIcon(HIGH_SPEED_VESSEL_ICON_COLOR);

    // Remove existing images if they exist
    if (map.hasImage('vessel-default')) map.removeImage('vessel-default');
    if (map.hasImage('vessel-slow')) map.removeImage('vessel-slow');
    if (map.hasImage('vessel-medium')) map.removeImage('vessel-medium');
    if (map.hasImage('vessel-fast')) map.removeImage('vessel-fast');
    if (map.hasImage('canal-pin')) map.removeImage('canal-pin');

    // Add new images
    map.addImage('vessel-default', defaultIcon);
    map.addImage('vessel-slow', slowIcon);
    map.addImage('vessel-medium', mediumIcon);
    map.addImage('vessel-fast', fastIcon);

    // Load canal pin icon
    const canalPinImg = new Image();
    canalPinImg.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = canalPinImg.width;
      canvas.height = canalPinImg.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(canalPinImg, 0, 0);
      map.addImage('canal-pin', {
        width: canalPinImg.width,
        height: canalPinImg.height,
        data: ctx.getImageData(0, 0, canalPinImg.width, canalPinImg.height).data
      });
    };
    canalPinImg.src = '/assets/images/blue-pin.svg';
  }, []);

  const handleClick = useCallback(
    (event) => {
      const feature = event.features?.[0];
      if (feature) {
        if (feature.layer.id === 'vessels-layer') {
          const vessel = feature.properties;
          const destinationPort = lookupPorts.find(
            (port) =>
              port.unlocode === vessel.destination ||
              port.unlocode === vessel.destinationPortUnlocode ||
              port.id === vessel.destinationPortId
          );

          setSelectedVessel({
            ...vessel,
            destinationPortUnlocode: destinationPort?.unlocode,
            destinationPortName: destinationPort?.portName
          });
        } else if (feature.layer.id === 'canals-circles' || feature.layer.id === 'canals-labels') {
          // Handle canal click
          const canal = {
            id: feature.properties.id,
            name: feature.properties.name,
            unlocode: feature.properties.unlocode,
            regionId: feature.properties.regionId,
            long: feature.geometry.coordinates[0],
            lat: feature.geometry.coordinates[1]
          };

          // If there's an onCanalClick prop, call it with the canal data
          if (typeof onCanalClick === 'function') {
            onCanalClick(canal);
          }
          setSelectedVessel(null);
        } else {
          setSelectedVessel(null);
        }
      } else {
        setSelectedVessel(null);
      }
    },
    [onCanalClick, lookupPorts]
  );

  const handleZoomIn = useCallback(() => {
    if (mapLoaded) {
      const currentZoom = viewport.zoom;
      setViewport({
        ...viewport,
        zoom: currentZoom + 1,
        transitionDuration: 300
      });
    }
  }, [viewport, mapLoaded]);

  const handleZoomOut = useCallback(() => {
    if (mapLoaded) {
      const currentZoom = viewport.zoom;
      setViewport({
        ...viewport,
        zoom: currentZoom - 1,
        transitionDuration: 300
      });
    }
  }, [viewport, mapLoaded]);

  const handleDurationChange = useCallback((days) => {
    setSelectedDays(days);
    setShowRoutes(!!days);
  }, []);

  return (
    <div
      ref={mapContainer}
      style={{
        position: 'relative',
        height: height,
        width: '100%'
      }}>
      {(!mapLoaded || loading) && vessels?.length && (
        <div
          style={{
            height: '100%',
            width: '100%',
            background: 'rgb(255 255 255 / 50%)',
            zIndex: 2,
            position: 'absolute'
          }}>
          <CircularProgress
            sx={{
              position: 'absolute',
              top: 'calc(50% - 50px)',
              left: 'calc(50% - 50px)'
            }}
            size={100}
          />
        </div>
      )}

      <Map
        key={key}
        {...viewport}
        onMove={(evt) => setViewport(evt.viewState)}
        onLoad={(event) => {
          handleMapLoad(event);
          updateMapIcons(event.target);
        }}
        mapStyle={
          themeMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11'
        }
        projection={projection}
        interactiveLayerIds={['vessels-layer', 'canals-circles', 'canals-labels', 'routes-layer']}
        onClick={handleClick}
        onMouseEnter={routeGeoJson && selectedDays && showRoutes ? onMouseEnterRoute : undefined}
        onMouseLeave={routeGeoJson && selectedDays && showRoutes ? onMouseLeaveRoute : undefined}
        style={{ width: '100%', height: '100%' }}>
        {routeGeoJson && selectedDays && showRoutes && (
          <Source id="routes" type="geojson" data={routeGeoJson}>
            <Layer
              id="routes-layer"
              type="line"
              paint={{
                'line-color': [
                  'case',
                  ['boolean', ['feature-state', 'hover'], false],
                  ['get', 'color'], // Use the original color for hovered route
                  hoveredRouteId !== null ? '#b0b0b0' : ['get', 'color'] // Gray for other routes when one is hovered
                ],
                'line-width': [
                  'case',
                  ['boolean', ['feature-state', 'hover'], false],
                  ROUTE_LINE_STYLES.DEFAULT.WIDTH * 2, // Double the width on hover
                  ROUTE_LINE_STYLES.DEFAULT.WIDTH
                ],
                'line-opacity': [
                  'case',
                  ['boolean', ['feature-state', 'hover'], false],
                  1, // Full opacity on hover
                  hoveredRouteId !== null ? 0.5 : ROUTE_LINE_STYLES.DEFAULT.OPACITY // Reduce opacity for non-hovered routes
                ],
                'line-dasharray': ROUTE_LINE_STYLES.DEFAULT.DASH_ARRAY
              }}
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
            />
          </Source>
        )}

        {portGeoJson && selectedDays && showPorts && (
          <Source id="ports" type="geojson" data={portGeoJson}>
            <Layer
              id="ports-circles"
              type="circle"
              paint={{
                'circle-radius': 3,
                'circle-color': '#80dfeb',
                'circle-opacity': 1
              }}
            />
            <Layer
              id="ports-labels"
              type="symbol"
              layout={{
                'text-field': ['get', 'name'],
                'text-anchor': 'top',
                'text-offset': [0, 1],
                'text-size': 10,
                'text-allow-overlap': false,
                'text-ignore-placement': false
              }}
              paint={{
                'text-color': themeMode ? '#fff' : '#000',
                'text-halo-color': themeMode ? '#000' : '#fff',
                'text-halo-width': 1
              }}
            />
          </Source>
        )}

        {canalsGeoJson && (
          <Source id="canals" type="geojson" data={canalsGeoJson}>
            <Layer
              id="canals-circles"
              type="symbol"
              layout={{
                'icon-image': 'canal-pin',
                'icon-size': 0.5,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom'
              }}
              paint={{
                'icon-color': '#4a90e2'
              }}
            />
            <Layer
              id="canals-labels"
              type="symbol"
              layout={{
                'text-field': ['get', 'name'],
                'text-anchor': 'top',
                'text-offset': [0, 0.5],
                'text-size': 12,
                'text-allow-overlap': false
              }}
              paint={{
                'text-color': themeMode ? '#fff' : '#000',
                'text-halo-color': themeMode ? '#000' : '#fff',
                'text-halo-width': 1
              }}
            />
          </Source>
        )}

        {selectedCanalPolygon && (
          <Source id="selected-canal-polygon" type="geojson" data={selectedCanalPolygon}>
            <Layer
              id="selected-canal-fill"
              type="fill"
              paint={{
                'fill-color': '#4a90e2',
                'fill-opacity': 0.2
              }}
            />
            <Layer
              id="selected-canal-outline"
              type="line"
              paint={{
                'line-color': '#4a90e2',
                'line-width': 2,
                'line-opacity': 0.8
              }}
            />
          </Source>
        )}

        {regionPolygonGeoJson && (
          <Source id="region-polygon" type="geojson" data={regionPolygonGeoJson}>
            <Layer
              id="region-fill"
              type="fill"
              paint={{
                'fill-color': '#4a90e2',
                'fill-opacity': 0.2
              }}
            />
            <Layer
              id="region-outline"
              type="line"
              paint={{
                'line-color': '#4a90e2',
                'line-width': 2,
                'line-opacity': 0.8
              }}
            />
          </Source>
        )}

        {mapLoaded && (
          <Source id="vessels" type="geojson" data={vesselGeoJson}>
            <Layer
              id="vessels-layer"
              type="symbol"
              layout={{
                'icon-image': ['get', 'iconImage'],
                'icon-size': 0.5,
                'icon-rotate': ['get', 'rotation'],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': showVesselsName ? ['get', 'vesselName'] : '',
                'text-anchor': 'top',
                'text-offset': [0, 1],
                'text-size': 10
              }}
              paint={{
                'icon-color': ['get', 'color'],
                'text-color': themeMode ? 'white' : 'black'
              }}
            />
          </Source>
        )}

        {selectedVessel && (
          <Popup
            longitude={Number(selectedVessel.long)}
            latitude={Number(selectedVessel.lat)}
            anchor="top"
            onClose={() => setSelectedVessel(null)}
            closeButton={false}
            className={`popup-details ${themeMode ? 'popup-dark' : 'popup-light'}`}
            closeOnClick={false}>
            {createPopupContent(selectedVessel, themeMode, getTimeZoneDisplay, uriExt)}
          </Popup>
        )}
      </Map>

      {showSpeedLegend && <VesselSpeedLegend />}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 1,
          border: `0.5px solid var(--dark-mode-border-divider-default, #7B7B7B)`,
          p: 1,
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 2
        }}>
        <Box position="relative">
          <ToolTip
            title="Vessel Filter"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.80)',
                  color: 'black'
                }
              }
            }}>
            <IconButton
              onClick={() => {
                setShowFilters((prev) => !prev);
                setShowSettings(false);
              }}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                border: `0.5px solid var(--dark-mode-border-divider-default, #7B7B7B)`,
                height: '32px',
                color: '#7B7B7B',
                width: '32px',
                mb: 1
              }}>
              <TuneIcon sx={{ fontSize: '24px', color: themeMode ? 'white' : 'black' }} />
            </IconButton>
          </ToolTip>
          {showFilters && (
            <Box
              sx={{
                width: '8px',
                height: '8px',
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                position: 'absolute',
                top: 0,
                right: 0
              }}
            />
          )}
        </Box>
        <Box position="relative">
          <ToolTip
            title="Map Settings"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.80)',
                  color: 'black'
                }
              }
            }}>
            <IconButton
              onClick={() => {
                setShowSettings((prev) => !prev);
                setShowFilters(false);
              }}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                border: `0.5px solid var(--dark-mode-border-divider-default, #7B7B7B)`,
                height: '32px',
                color: '#7B7B7B',
                width: '32px'
              }}>
              <SettingsOutlinedIcon
                sx={{ fontSize: '24px', color: themeMode ? 'white' : 'black' }}
              />
            </IconButton>
          </ToolTip>
          {showSettings && (
            <Box
              sx={{
                width: '8px',
                height: '8px',
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                position: 'absolute',
                top: 0,
                right: 0
              }}
            />
          )}
        </Box>
      </Box>

      {showSettings && (
        <Box
          sx={{
            position: 'absolute',
            top: '16px',
            right: '64px',
            zIndex: 2
          }}>
          <VesselsMapSettings
            onClose={() => setShowSettings(false)}
            showVesselsName={showVesselsName}
            onChangeShowVesselsName={setShowVesselsName}
            showPorts={showPorts}
            onTogglePorts={setShowPorts}
          />
        </Box>
      )}

      {showFilters && (
        <Box
          sx={{
            position: 'absolute',
            top: '16px',
            right: '64px',
            zIndex: 2
          }}>
          <VesselsMapFilter
            onClose={() => setShowFilters(false)}
            vessels={vessels}
            selectedVessels={selectedVessels}
            onVesselSelectionChange={setSelectedVessels}
          />
        </Box>
      )}

      {allowFullscreen && (
        <div style={mapFullscreenButtonParentStyle}>
          <MapFullscreenButton
            onClick={() => {
              setSearchParams((prev) => {
                prev.set(OVERVIEW_MAP_FULLSCREEN_PARAM, !isFullscreenFromUrl);
                return prev;
              });
            }}
            isFullScreen={isFullscreenFromUrl || isFullscreen}
          />
        </div>
      )}

      <DashboardActivityMapZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
      <VesselsMapRouteDurationSelector
        selectedDays={selectedDays}
        onDurationChange={handleDurationChange}
        isLoading={isLoadingTransitDetails}
        enabled={true}
      />
    </div>
  );
}

VesselsMap.propTypes = {
  vessels: PropTypes.arrayOf(PropTypes.object),
  regionPolygon: PropTypes.string,
  projection: PropTypes.oneOf([
    'globe',
    'equalEarth',
    'naturalEarth',
    'winkelTripel',
    'albers',
    'lambertConformalConic',
    'equirectangular',
    'mercator'
  ]),
  canals: PropTypes.arrayOf(PropTypes.object),
  height: PropTypes.string,
  mapStyle: PropTypes.string,
  zoom: PropTypes.number,
  center: PropTypes.arrayOf(PropTypes.number),
  loading: PropTypes.bool,
  showSpeedLegend: PropTypes.bool,
  showVesselList: PropTypes.bool,
  isCanal: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  onChangeFullscreen: PropTypes.func,
  allowFullscreen: PropTypes.bool,
  selectedCanal: PropTypes.object,
  onCanalClick: PropTypes.func
};

export default VesselsMap;
